import type { OrganizationUserInfoDto } from '@/types/api/user';
import i18n from '@/i18n';
import { formatDateShort } from './dateUtils';

const ACCOUNT_ROLE_LABEL_FALLBACK: Record<string, string> = {
	guest: 'Guest',
	student: 'Student',
	master: 'Master',
	admin: 'Admin',
};

export type Role = 'guest' | 'student' | 'master' | 'admin';

/**
 * 백엔드 role 숫자를 프론트엔드 role 문자열로 변환
 * 
 * @param roleNumber - 백엔드에서 반환하는 role 숫자 (0: guest, 1: student, 2: master, 5: admin)
 * @returns 프론트엔드 role 문자열 ('guest' | 'student' | 'master' | 'admin')
 * 
 * @example
 * convertRoleNumberToString(0) // 'guest'
 * convertRoleNumberToString(1) // 'student'
 * convertRoleNumberToString(2) // 'master'
 * convertRoleNumberToString(5) // 'admin'
 */
export const convertRoleNumberToString = (roleNumber: number): Role => {
	const roleMap: Record<number, Role> = {
		0: 'guest',
		1: 'student',
		2: 'master',
		4: 'admin', // OpenAPI: sales_admin → UI는 admin 배지와 동일 취급
		5: 'admin',
	};
	
	return roleMap[roleNumber] || 'guest';
};

/** 기관 사용자 목록(UserList 등) 테이블에 표시할 역할 라벨 */
export type OrganizationUserListRoleLabel = 'Guest' | 'Master' | 'Student' | 'Admin';

/** {@link normalizeOrganizationUserAccountType} 보조 신호 — 목록 DTO의 role·만료일·이메일 인증 여부 */
export type NormalizeOrganizationUserAccountTypeOptions = {
	role?: number | string | null;
	/** 값이 비어 있지 않으면 게스트(ASSOCIATE) 계정으로 본다 */
	guestAccountExpiresAt?: string | null;
	/** 명시 시 게스트/정회원 구분의 단일 기준(목록 API가 내려줄 때) */
	isEmailVerified?: boolean | null;
};

/** 목록 DTO에서 이메일 인증 여부 추출(camelCase·스네이크 병행) */
export function readOrganizationUserEmailVerified(dto: OrganizationUserInfoDto): boolean | undefined {
	const r = dto as OrganizationUserInfoDto & Record<string, unknown>;
	const v = r.isEmailVerified ?? r.emailVerified ?? r.is_email_verified ?? r.email_verified;
	if (typeof v === 'boolean') return v;
	if (v === 'true') return true;
	if (v === 'false') return false;
	return undefined;
}

export function organizationUserAccountTypeNormalizeOptionsFromDto(
	dto: OrganizationUserInfoDto
): NormalizeOrganizationUserAccountTypeOptions {
	return {
		role: dto.role,
		guestAccountExpiresAt: dto.guestAccountExpiresAt,
		isEmailVerified: readOrganizationUserEmailVerified(dto),
	};
}

export function resolveOrganizationUserAccountKind(dto: OrganizationUserInfoDto): 'REGULAR' | 'ASSOCIATE' {
	return normalizeOrganizationUserAccountType(dto.accountType, organizationUserAccountTypeNormalizeOptionsFromDto(dto));
}

/**
 * 기관 사용자 목록 API의 accountType 런타임 값 정규화.
 * - 이메일 인증 여부가 오면 **그 값만**으로 게스트(미인증)·정회원(인증) 구분 (그 외 추정 제거)
 * - 인증 필드가 없으면: `guestAccountExpiresAt`, accountType 토큰, role 0 등 기존 보조 규칙 사용
 */
export function normalizeOrganizationUserAccountType(
	raw: unknown,
	options?: NormalizeOrganizationUserAccountTypeOptions
): 'REGULAR' | 'ASSOCIATE' {
	const verified = options?.isEmailVerified;
	if (verified === true) return 'REGULAR';
	if (verified === false) return 'ASSOCIATE';

	const expiry = options?.guestAccountExpiresAt;
	if (typeof expiry === 'string' && expiry.trim() !== '') {
		return 'ASSOCIATE';
	}

	const s = String(raw ?? '').trim().toUpperCase();
	if (s === 'ASSOCIATE' || s === 'GUEST') return 'ASSOCIATE';
	if (s === 'REGULAR' || s === 'MEMBER') return 'REGULAR';

	const role = options?.role;
	const roleIsGuestZero = typeof role === 'number' && Number.isFinite(role) && role === 0;

	if (!s || s === 'NULL' || s === 'UNDEFINED') {
		return roleIsGuestZero ? 'ASSOCIATE' : 'REGULAR';
	}

	if (roleIsGuestZero) return 'ASSOCIATE';
	return 'REGULAR';
}

/**
 * GET /user/organization/:id/list 의 OrganizationUserInfoDto를 UI 라벨로 변환.
 * - accountType ASSOCIATE → Guest
 * - 문자열 ORGANIZATION_ADMIN / MASTER → Master (기관 관리자·플랫폼 마스터)
 * - 숫자 role은 프로필과 동일 (0 게스트, 1 수강생(student), 2 마스터, 4·5 관리)
 */
export function organizationUserListRoleLabel(dto: OrganizationUserInfoDto): OrganizationUserListRoleLabel {
	if (resolveOrganizationUserAccountKind(dto) === 'ASSOCIATE') return 'Guest';
	const r = dto.role;
	if (r === 'ORGANIZATION_ADMIN' || r === 'MASTER') return 'Master';
	if (typeof r === 'number' && Number.isFinite(r)) {
		const mapped = convertRoleNumberToString(r);
		if (mapped === 'master') return 'Master';
		if (mapped === 'admin') return 'Admin';
		if (mapped === 'guest') return 'Guest';
		return 'Student';
	}
	if (r === 'SALES_ADMIN' || r === 'SUPER_ADMIN') return 'Admin';
	if (r === 'STUDENT') return 'Student';
	return 'Student';
}

/** 양의 정수 organizationId만 기관 소속으로 본다(0·null·미포함은 무소속). */
function isInstitutionalOrganizationId(organizationId: unknown): boolean {
	if (organizationId == null) return false;
	const n = Number(organizationId);
	return Number.isFinite(n) && n > 0;
}

/**
 * GET /user/profile 등으로 받은 정보로 UI용 license 키 결정.
 * - 최초 가입 후 소속 없이 정회원(Student)만 된 경우 → `free`
 * - Student + 유효한 기관 id → `pro_univ_student` (기관 Pro 라인)
 */
export function resolveLicenseFromProfile(userProfile: {
	role: number;
	organizationId?: number | null;
}): string {
	const userRole = convertRoleNumberToString(userProfile.role);
	if (userRole === 'guest') return 'free';
	if (userRole === 'master') return 'pro_univ_master';
	if (userRole === 'admin') return 'admin';
	return isInstitutionalOrganizationId(userProfile.organizationId) ? 'pro_univ_student' : 'free';
}

export const getRoleBadgeClasses = (role: string): string => {
    const classes: Record<string, string> = {
        guest: 'text-green-600 border-green-600 bg-green-50',
        student: 'text-blue-500 border-blue-500 bg-blue-50',
        master: 'text-purple-600 border-purple-600 bg-purple-50',
        admin: 'text-red-500 border-red-500 bg-red-50'
    };
    return classes[role] || 'text-gray-500 border-gray-500 bg-gray-50';
};

export const getRoleLabel = (role: string): string => {
	const rawTrimmed = String(role ?? '').trim();
	const key = rawTrimmed.toLowerCase();
	if (!key) return '';
	const fallback = ACCOUNT_ROLE_LABEL_FALLBACK[key] ?? rawTrimmed;
	return String(
		i18n.t(`settings.roles.${key}`, {
			ns: 'auth',
			defaultValue: fallback,
		})
	);
};

export type LicenseType = 'pro' | 'pro_class' | 'basic_personal' | 'basic_class' | 'pro_univ_student' | 'pro_univ_master';

/**
 * 게스트 만료일 포맷팅 함수
 * @param expirationDate - 백엔드에서 받은 만료일 (ISO 8601) 또는 null
 * @returns 포맷팅된 만료일 문자열 (로케일 규칙)
 * @note 백엔드 작업 완료 전까지는 임시로 "오늘 + 7일" 반환
 */
export const getGuestExpirationDate = (expirationDate?: string | null, locale?: string | null): string => {
    if (expirationDate) {
        return formatDateShort(expirationDate, locale);
    }
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return formatDateShort(date.toISOString(), locale);
};

// License-based feature access for Master users
export const isProLicense = (license?: LicenseType): boolean => {
    if (!license) return false;
    return license.startsWith('pro');
};

export const hasStudioAccess = (role: Role, license?: LicenseType): boolean => {
    return role === 'admin' || (role === 'master' && isProLicense(license));
};

export const hasNaturalLanguageAccess = (role: Role, license?: LicenseType): boolean => {
    return role === 'admin' || (role === 'master' && isProLicense(license));
};
